import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';

import { AUTOMATED_EXECUTION_USER } from '@qality/common-components/src/picker/UserPicker';
import { IS_CLOUD } from '@qality/common-config';
import { Assignee, User } from '@qality/common-model';
import { UserOption } from '@qality/common-model/src/UserOption';
import {
  StorageOfUniqueModels,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { UserFactory, Utils } from '@qality/common-utils';

export type NewUserApplicationInfoResponse = {
  newUser: boolean;
};

type PermissionCheckResponse = {
  permissions: Record<
    string,
    {
      description: string;
      havePermission: boolean;
      id: string;
      key: string;
      name: string;
      type: string;
    }
  >;
};

const MAX_ASYNC_REQUESTS = 10;
const AUTHENTICATION_FAILED_CODE = 401;
const NO_PERMISSION_FAILED_CODE = 403;
const NOT_FOUND_CODE = 404;

export default {
  async fetchIsNewUser(): Promise<boolean> {
    try {
      const url: string = await StorageOfUniqueServices.get(
        'AuthenticateService'
      ).authenticateByJWT(
        StorageOfUniqueServices.get('WindowOriginService').origin +
          StorageOfUniqueServices.get('APIConstants').URL_APPLICATION_INFO
      );

      const response: NewUserApplicationInfoResponse = (await axios.get(url))
        .data;
      return response.newUser;
    } catch (e) {
      return false;
    }
  },

  async getUsersForPicker(
    query = '',
    shouldAddAutomatedUser = false
  ): Promise<Array<User>> {
    const UserImpl = StorageOfUniqueModels.get('User');

    const response = await StorageOfUniqueServices.get('API').request({
      url:
        '/rest/api/2/user/picker?showAvatar=true&query=' +
        (!query ? '.' : query),
      type: 'GET',
      header: 'Accept: application/json',
    });

    const users = JSON.parse(response.body).users.map((user) => {
      return this.createSelectUserObject(new UserImpl(user));
    });

    addAutomatedExecutionUser(users, query, shouldAddAutomatedUser);

    return users;
  },

  async getAssignableToProjectUserOptions(
    projectKey: string
  ): Promise<Array<User>> {
    const UserImpl = StorageOfUniqueModels.get('User');
    const response = await this.findUsersAssignableToProject(projectKey);
    return response.map((user) => {
      return this.createSelectUserObject(new UserImpl(user));
    });
  },

  async findUsersAssignableToProject(
    projectKey: string,
    query = null
  ): Promise<Array<User>> {
    const queryValue = query ? query : IS_CLOUD ? '.' : '';
    const UserImpl = StorageOfUniqueModels.get('User');
    const queryParamName = IS_CLOUD ? 'query' : 'username';
    const response = await StorageOfUniqueServices.get('API').request({
      url: `/rest/api/2/user/assignable/search?${queryParamName}=${queryValue}&project=${projectKey}`,
      type: 'GET',
      header: 'Accept: application/json',
    });
    const users = JSON.parse(response.body);
    return users.map((user) => new UserImpl(user));
  },

  async getCurrentUser(): Promise<User> {
    return StorageOfUniqueServices.get('API')
      .request('/rest/api/2/myself')
      .then((data) => {
        const userImpl = StorageOfUniqueModels.get('User');
        return new userImpl(JSON.parse(data.body));
      })
      .catch((err) => {
        if (err.xhr.status === AUTHENTICATION_FAILED_CODE) {
          return UserFactory.createAnonymousUser();
        }
        throw err;
      });
  },

  async getCurrentUserPermissionCheck(
    permission: string
  ): Promise<PermissionCheckResponse> {
    const response = await StorageOfUniqueServices.get('API').request(
      `/rest/api/2/mypermissions?permissions=${permission}`
    );

    return JSON.parse(response.body);
  },

  async getCurrentUserAdministerPermissions(): Promise<boolean> {
    const { permissions } = await this.getCurrentUserPermissionCheck(
      'ADMINISTER'
    );
    return permissions.ADMINISTER.havePermission;
  },

  getUserById(userId: string): User {
    const UserImpl = StorageOfUniqueModels.get('User');
    //In some cases(test execution view only?) anonymous user can visit page and assign himself to something.
    //In that case the id will be anonymous
    if (userId === 'anonymous_user' || userId === 'anonymous') {
      return UserFactory.createAnonymousUser();
    }

    if (userId === AUTOMATED_EXECUTION_USER.value) {
      return UserFactory.createAutomatedExecutionUser();
    }

    if (!userId) {
      return null;
    }

    return StorageOfUniqueServices.get('API')
      .request(
        `/rest/api/2/user?${StorageOfUniqueModels.get('IDENTIFIER')}=${userId}`
      )
      .then((data) => {
        return new UserImpl(JSON.parse(data.body));
      })
      .catch((err) => {
        if (
          err.xhr.status === AUTHENTICATION_FAILED_CODE ||
          err.xhr.status === NO_PERMISSION_FAILED_CODE
        ) {
          return UserFactory.createSomeUser();
        }
        if (err.xhr.status === NOT_FOUND_CODE) {
          return UserFactory.createRemovedUser();
        }
        throw err;
      });
  },

  async retrieveUserSelectObjectById(userId: string): Promise<UserOption> {
    if (!userId) {
      return null;
    }
    const user = await this.getUserById(userId);
    if (
      user.getDisplayName() === UserFactory.createSomeUser().getDisplayName() ||
      user.getDisplayName() ===
        UserFactory.createAnonymousUser().getDisplayName()
    ) {
      return null;
    } else {
      return this.createSelectUserObject(user);
    }
  },

  createSelectUserObject(user: User): {
    label: string;
    value: string;
    avatar: string;
    user: User;
  } {
    return {
      label: user.getDisplayName(),
      value: user.getIdentifier(),
      avatar: user.getAvatar(),
      user,
    };
  },

  async fetchAssignee(userId: string): Promise<User> {
    if (!userId) {
      return;
    } else {
      return this.getUserById(userId);
    }
  },

  //TODO: currently endpoint to bulk fetch users is "experimental", we have to do it manually
  async fetchAssignees(userIds: Array<string>): Promise<Array<any>> {
    let assignees = [];
    const numberOfRequestPackages = Math.ceil(
      userIds.length / MAX_ASYNC_REQUESTS
    );
    for (let i = 0; i < numberOfRequestPackages; i++) {
      const promises = [];
      for (let j = 0; j < MAX_ASYNC_REQUESTS; j++) {
        const userIdIndex = i * MAX_ASYNC_REQUESTS + j;
        if (userIdIndex < userIds.length) {
          promises.push(
            (async () => {
              const assignee = await this.fetchAssignee(userIds[userIdIndex]);
              return overwriteIds(assignee, userIds[userIdIndex]);
            })()
          );
        }
      }
      const someAssignees = await Promise.all(promises);
      assignees = assignees.concat(someAssignees);
    }

    return assignees;
  },

  getAssignee(id: string, users: { [key: string]: User }): User {
    return Utils.isNilOrEmpty(id)
      ? UserFactory.createUnassignedUser()
      : Utils.isNilOrEmpty(users[id])
      ? UserFactory.createRemovedUser()
      : users[id];
  },
};

function overwriteIds(userObject: any, id: string) {
  const user = cloneDeep(userObject);
  user.key = id;
  user.accountId = id;
  return user;
}

function addAutomatedExecutionUser(
  users: Array<Assignee>,
  query: string,
  shouldAddAutomatedUser: boolean
): Array<Assignee> {
  if (shouldAddAutomatedUser) {
    users.push(AUTOMATED_EXECUTION_USER);
    users.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );

    const foundUserIndex = users.findIndex(
      (user) => user.label.toLowerCase() === query.toLowerCase()
    );

    if (foundUserIndex !== -1) {
      const foundUser = users[foundUserIndex];
      users.splice(foundUserIndex, 1);
      users.unshift(foundUser);
    }
  }
  return users;
}
